import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { formatDate } from "@bw/shared/utils";

import { PlatformPage } from "@/components/PlatformPage";
import { StatusBadge } from "@/components/StatusBadge";
import { orgBusinessName, orgLegalName } from "@/shared/orgDisplay";

import { listOrgs } from "../api";

const STATUS_FILTERS = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "In Review", value: "in_review" },
  { label: "Approved", value: "approved" },
  { label: "Active", value: "active" },
  { label: "Rejected", value: "rejected" },
  { label: "Suspended", value: "suspended" },
] as const;

const PAGE_SIZE = 20;
type StatusFilter = (typeof STATUS_FILTERS)[number]["value"];

function formatStatus(status: string): string {
  return status.replace(/_/g, " ");
}

function paginationLabel(type: string, page: number | null): string {
  if (type === "next") return "Next";
  if (type === "previous") return "Previous";
  if (type === "first") return "First";
  if (type === "last") return "Last";
  return page === null ? "More pages" : `Page ${page}`;
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography color="text.secondary" variant="overline">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
}

export function OrgListPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const statusFilter = activeFilter === "all" ? undefined : activeFilter;
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["platform", "orgs", statusFilter, debouncedSearch, offset],
    queryFn: () =>
      listOrgs({
        status: statusFilter,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (!data) return;
    const lastOffset =
      data.total <= 0
        ? 0
        : Math.floor((data.total - 1) / PAGE_SIZE) * PAGE_SIZE;
    if (offset > lastOffset) setOffset(lastOffset);
  }, [data, offset]);

  return (
    <PlatformPage title="Organizations">
      <Stack spacing={2.5}>
        <Paper
          component="section"
          aria-label="Organization filters"
          variant="outlined"
          sx={{ p: 2 }}
        >
          <Stack spacing={1.5}>
            <TextField
              type="text"
              size="small"
              fullWidth
              label="Search organizations"
              placeholder="Search business name..."
              value={search}
              slotProps={{
                htmlInput: { "aria-label": "Search organizations" },
              }}
              onChange={(event) => {
                setSearch(event.target.value);
                setOffset(0);
              }}
            />
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{ flexWrap: "wrap" }}
            >
              {STATUS_FILTERS.map((filter) => (
                <Button
                  key={filter.value}
                  type="button"
                  size="small"
                  variant={
                    activeFilter === filter.value ? "contained" : "outlined"
                  }
                  aria-pressed={activeFilter === filter.value}
                  onClick={() => {
                    setActiveFilter(filter.value);
                    setOffset(0);
                  }}
                >
                  {filter.label}
                </Button>
              ))}
            </Stack>
          </Stack>
        </Paper>

        {isError ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Could not load organizations. Try again.
          </Alert>
        ) : isLoading ? (
          <Box
            role="status"
            aria-label="Loading organizations"
            aria-live="polite"
            sx={{ display: "flex", justifyContent: "center", py: 8 }}
          >
            <CircularProgress size={36} />
          </Box>
        ) : items.length === 0 ? (
          <Box
            role="status"
            aria-live="polite"
            sx={{ py: 6, textAlign: "center" }}
          >
            <Typography color="text.secondary">
              No organizations matched this view
            </Typography>
          </Box>
        ) : (
          <>
            <Typography color="text.secondary" variant="body2">
              {total.toLocaleString()}{" "}
              {total === 1 ? "organization" : "organizations"}
            </Typography>

            <Stack spacing={1.5}>
              {items.map((org) => {
                const businessName = orgBusinessName(org);
                const ownerName =
                  org.owner_display_name?.trim() ||
                  org.owner_full_name?.trim() ||
                  "Owner unavailable";

                return (
                  <Card key={org.org_id} variant="outlined">
                    <CardActionArea
                      onClick={() => navigate(`/organizations/${org.org_id}`)}
                      sx={{ textAlign: "left" }}
                    >
                      <CardContent>
                        <Stack spacing={2.5}>
                          <Box>
                            <Stack
                              direction="row"
                              spacing={1.25}
                              useFlexGap
                              sx={{ alignItems: "center", flexWrap: "wrap" }}
                            >
                              <Typography
                                component="h2"
                                variant="h6"
                                sx={{ fontWeight: 700 }}
                              >
                                {businessName}
                              </Typography>
                              <StatusBadge status={org.status} />
                            </Stack>
                            <Typography
                              color="text.secondary"
                              variant="body2"
                              sx={{ mt: 0.5 }}
                            >
                              {orgLegalName(org) ?? "Legal name not provided"}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                lg: "repeat(4, minmax(0, 1fr))",
                              },
                              gap: 2,
                            }}
                          >
                            <Fact label="Owner" value={ownerName} />
                            <Fact
                              label="Lifecycle"
                              value={formatStatus(org.status)}
                            />
                            <Fact
                              label="Created"
                              value={formatDate(org.created_at)}
                            />
                            <Fact
                              label="Tax ID"
                              value={org.tax_id ?? "Not provided"}
                            />
                          </Box>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>

            {total > PAGE_SIZE && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ alignItems: "center", justifyContent: "space-between" }}
                >
                  <Typography color="text.secondary" variant="body2">
                    Page {page} of {pageCount}
                  </Typography>
                  <Pagination
                    page={page}
                    count={pageCount}
                    size="small"
                    getItemAriaLabel={(type, itemPage) =>
                      paginationLabel(type, itemPage)
                    }
                    onChange={(_event, nextPage) =>
                      setOffset((nextPage - 1) * PAGE_SIZE)
                    }
                  />
                </Stack>
              </Paper>
            )}
          </>
        )}
      </Stack>
    </PlatformPage>
  );
}
